import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LazyMotion, domAnimation, AnimatePresence } from 'framer-motion';
import Intro from './components/Intro';
import Desktop from './components/Desktop';
import MobileApp from './components/MobileApp';
import GuidedTour from './components/GuidedTour';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import EasterEggNotification from './components/EasterEggNotification';
import MatrixRain from './components/MatrixRain';
import BSOD from './components/BSOD';
import ScreenSaver from './components/ScreenSaver';
import ShutdownScreen from './components/ShutdownScreen';
import MinimalistSite from './components/MinimalistSite';
import Win98Desktop from './components/Win98Desktop';
import ProposalPage from './components/ProposalPage';
import CampaignPage from './components/CampaignPage';
import AdminLogin from './components/AdminLogin';
import AdminLeads from './components/AdminLeads';
import AdminAnalytics from './components/AdminAnalytics';
import Analytics from './components/Analytics';
import CookieConsent from './components/CookieConsent';
import ChatBubble from './components/ChatBubble';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Sound effects - Base64 encoded short sounds
export const SOUNDS = {
  startup: 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2LkZWTi4B0Z2Rxf4yXnJmRhXZpZHB/jpqgn5eSgnRnY3B/jpqgn5eSgnRnY3F/jpqgn5eSgnRnY3F/jpuhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoY3F/j5uhoBKUhHRoZHF/j5uhoBGThHNnYnB+jpqgnpGSgnRmYm9+jZmfnpCRgXNlYW5+jJienpCRgXNlYW5+jJienpCRgXNlYW5+jJienpCRgXNlYW5+jJienpCRgXNlYW5+jJienpCRgXNlYW5+jJifnpCQgHJkYG19i5een4+PgHFjX2x8ipednI6OgHBiXmt7iZWcm42MfnBhXWp6iJScm4yLfW9gXGl5h5ObmouKfG5fW2h4hpKamYqJe21eWmd3hZGZmImIemxdWWZ2hJCYl4iHemtcWGV1g4+XloaGeWpbV2R0goKYlYWFd2lZVmNzgYKXlISEdmhYVWJygYGWk4ODb2dXVGFxgICVkoKCbmZWU2BwgH+UkYGBbWVVUl9vf3+TkICAa2RUUl5ufn6SkH9/aWNTUVxte3yQjn19Z2FSUE5se3uPjXx8ZmBRUE1renqOjHt7ZV9QT0xqeXmNi3p5ZF5PTkppeHiMin15Y11OTklod3eLiXh4Yl1NTUhndnaMiHd3YVxMTEdmdXWLh3Z2YFtLTEZlcHWKhnV1X1pLSwRkb3SJhXR0XllKSgNjbnOIhHNzXVhJSQJibXKHg3JyXFdISAFhbHGGgnFxW1ZHRwBga3CFAW9wWlVGRv9fanCEAW5vWVRFRf5eaW+DAG1uWFNERP1dZ26CAGxtV1JDQ/xcZm2BAGtrVlFCQvtbZWyA/2pqVVBBQfpaZGt//2lpVE9AQPdZZGp+/mhoU04/P/VYZG1+/WdnUk0/PvRYY2x9/GZmUUw+PfNXYmt8+2VlUEs9PPJWYWp7+mRkT0o8O/BWYGl6+WNjTkk7OvBVX2h5+GJiTUg6OfBUXmd4+GFhTEY5OO9TXmZ392BgS0Y4N+5TXmV291/fSkU3Nu1SXmR19l7eSUQzNexRXWN09F3dSEMyNOtQXGJz811bR0ExNOpPWmFy8lxaRj8wM+lPWWBx8VtZRT4vMuhOWF9v8FpYRD0uMedNV15u7llXQzwtMOZMVl1t7VhWQjssMOVLVVxs7FdVQTorL+RKVFtr61ZUQDkqLuNJU1pp6lVTQDgoLeJIUllp6VRSPzcnLOFHUlhn6FNRPjYmK+BGUVdm51JPPTUlKt9FUFZl5lFOPDQkKd5ET1Vk5VBNOzMjKN1DTlRj5E9MOjIiJ9xCTVNi409LOTEhJtpBTE9h4U5KODAhJdlATU5g30xJNi8gJNg/TEte3ktINS4fI9c+S0pd3UpHNC0eItY9Skpc2klGMyweIdU8SUlb10hFMiwdINU7SEla1kdEMSscINQ6R0dZ1UZDMCob',
  click: 'data:audio/wav;base64,UklGRl9vT19teleVkpGRkZGRkZGRkZGQkJCQkJCPj4+Pj4+Ojo6Ojo6NjY2NjY2MjIyMjIyLi4uLi4uKioqKioqJiYmJiYmIiIiIiIiHh4eHh4eGhoaGhoaFhYWFhYWEhISE',
  windowOpen: 'data:audio/wav;base64,UklGRl9vT19teleVjIyMjI2NjY6Ojo+Pj5CQkJGRkZKSkpOTk5SUlJWVlZaWlpeXl5iYmJmZmZqampubm5ycnJ2dnZ6enp+fn6CgoKGhoaKioqOjo6SkpKWlpaampqenp6ioqKmpqaqqqqurq6ysrK2tra6urq+vr7CwsLGxsbKysrOzs7S0tA==',
  windowClose: 'data:audio/wav;base64,UklGRl9vT19teleVtLS0s7OzsrKysbGxsLCwr6+vrq6ura2trKysq6urqqqqiYmJiYqKioqLi4uLjIyMjI2NjY6Ojo+Pj5CQkJGRkZKSkpOTk5SUlJWVlZaWlpeXl5iYmJmZmZqampubm5ycnJ2dnZ6enp+fn6CgoA==',
  error: 'data:audio/wav;base64,UklGRl9vT19teleVoKCgn5+fnp6enZ2dnJycm5ubmpqamZmZmJiYl5eXlpaWlZWVlJSUk5OTkpKSkZGRkJCQj4+Pjo6OjY2NjIyMi4uLioqKiYmJiIiIh4eHhoaGhYWFhISEg4ODgoKCgYGBgICA',
  notify: 'data:audio/wav;base64,UklGRl9vT19teleVgICAgYGBgoKCg4ODhISEhYWFhoaGh4eHiIiIiYmJioqKi4uLjIyMjY2Njo6Oj4+PkJCQkZGRkpKSk5OTlJSUlZWVlpaWl5eXmJiYmZmZmpqam5ubnJycnZ2dnp6en5+f'
};

export const playSound = (soundData, volume = 0.15) => {
  try {
    const audio = new Audio(soundData);
    audio.volume = volume;
    audio.play().catch(() => {});
  } catch (e) {}
};

// Konami code sequence
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      // Check viewport width and touch capability
      const isMobileWidth = window.innerWidth < 768;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsMobile(isMobileWidth || (isTouchDevice && window.innerWidth < 1024));
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

function MainApp() {
  const isMobile = useIsMobile();
  const [introComplete, setIntroComplete] = useState(false);
  const [showTour, setShowTour] = useState(false);
  
  // Don't auto-open welcome window on first visit - WelcomeGuide handles that
  const isFirstVisit = !localStorage.getItem('welcomeGuideShown');
  
  const [windows, setWindows] = useState({
    welcome: { open: !isFirstVisit, minimized: false, zIndex: 1, position: { x: 180, y: 60 } },
    readme: { open: false, minimized: false, zIndex: 0, position: { x: 140, y: 80 } },
    services: { open: false, minimized: false, zIndex: 0, position: { x: 200, y: 100 } },
    metrics: { open: false, minimized: false, zIndex: 0, position: { x: 260, y: 120 } },
    about: { open: false, minimized: false, zIndex: 0, position: { x: 320, y: 140 } },
    contact: { open: false, minimized: false, zIndex: 0, position: { x: 380, y: 80 } },
    recycle: { open: false, minimized: false, zIndex: 0, position: { x: 440, y: 100 } },
    minesweeper: { open: false, minimized: false, zIndex: 0, position: { x: 500, y: 120 } },
    cmd: { open: false, minimized: false, zIndex: 0, position: { x: 180, y: 160 } },
    video: { open: false, minimized: false, zIndex: 0, position: { x: 500, y: 60 } },
    appwizard: { open: false, minimized: false, zIndex: 0, position: { x: 250, y: 50 } },
    winamp: { open: false, minimized: false, zIndex: 0, position: { x: 550, y: 80 } },
    mycomputer: { open: false, minimized: false, zIndex: 0, position: { x: 200, y: 80 } },
    network: { open: false, minimized: false, zIndex: 0, position: { x: 250, y: 100 } },
    internet: { open: false, minimized: false, zIndex: 0, position: { x: 300, y: 120 } },
  });
  const [maxZ, setMaxZ] = useState(1);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showBSOD, setShowBSOD] = useState(false);
  const [showScreenSaver, setShowScreenSaver] = useState(false);
  const [isShutdown, setIsShutdown] = useState(false);
  const [volume, setVolume] = useState(50);
  const [cursorStyle, setCursorStyle] = useState('default');
  const [konamiIndex, setKonamiIndex] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [crtEnabled, setCrtEnabled] = useState(true);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const [currentEasterEgg, setCurrentEasterEgg] = useState(null);

  // Check if intro has been shown
  useEffect(() => {
    const hasSeenIntro = sessionStorage.getItem('introSeen');
    if (hasSeenIntro) {
      setIntroComplete(true);
    }
  }, []);

  // Check if tour should be shown after intro completes
  // REMOVED: Old GuidedTour - now using WelcomeGuide in Desktop.jsx instead
  // The WelcomeGuide shows automatically for first-time visitors

  // Keyboard shortcuts listener (including "?" for help)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Show keyboard shortcuts on "?"
      if (e.key === '?' && !showTour && introComplete) {
        setShowKeyboardHelp(true);
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showTour, introComplete]);

  // Konami code listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      setLastActivity(Date.now());
      
      if (e.key === KONAMI_CODE[konamiIndex]) {
        const newIndex = konamiIndex + 1;
        if (newIndex === KONAMI_CODE.length) {
          setShowMatrix(true);
          setKonamiIndex(0);
          setCurrentEasterEgg('konami');
          playSound(SOUNDS.notify);
        } else {
          setKonamiIndex(newIndex);
        }
      } else {
        setKonamiIndex(0);
      }

      // Alt+Tab window switching
      if (e.altKey && e.key === 'Tab') {
        e.preventDefault();
        const openWindowIds = Object.entries(windows)
          .filter(([_, w]) => w.open)
          .sort((a, b) => b[1].zIndex - a[1].zIndex)
          .map(([id]) => id);
        
        if (openWindowIds.length > 1) {
          const currentIndex = 0;
          const nextId = openWindowIds[(currentIndex + 1) % openWindowIds.length];
          focusWindow(nextId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [konamiIndex, windows]);

  // Screen saver after inactivity
  useEffect(() => {
    if (!introComplete || showMatrix || showBSOD || isShutdown) return;

    const checkInactivity = () => {
      const inactiveTime = Date.now() - lastActivity;
      if (inactiveTime > 120000 && !showScreenSaver) { // 2 minutes
        setShowScreenSaver(true);
      }
    };

    const interval = setInterval(checkInactivity, 10000);
    
    const resetActivity = () => setLastActivity(Date.now());
    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('click', resetActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('click', resetActivity);
    };
  }, [introComplete, showMatrix, showBSOD, isShutdown, showScreenSaver, lastActivity]);

  const handleIntroComplete = () => {
    sessionStorage.setItem('introSeen', 'true');
    setIntroComplete(true);
    playSound(SOUNDS.startup, volume / 100 * 0.3);
  };

  const openWindow = useCallback((id) => {
    setCursorStyle('wait');
    playSound(SOUNDS.windowOpen, volume / 100 * 0.15);
    
    setTimeout(() => {
      setMaxZ(prev => prev + 1);
      setWindows(prev => ({
        ...prev,
        [id]: { ...prev[id], open: true, minimized: false, zIndex: maxZ + 1 }
      }));
      setCursorStyle('default');
    }, 300);
  }, [maxZ, volume]);

  const closeWindow = useCallback((id) => {
    playSound(SOUNDS.windowClose, volume / 100 * 0.15);
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], open: false }
    }));
  }, [volume]);

  const minimizeWindow = useCallback((id) => {
    playSound(SOUNDS.click, volume / 100 * 0.15);
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], minimized: true }
    }));
  }, [volume]);

  const focusWindow = useCallback((id) => {
    setMaxZ(prev => prev + 1);
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], minimized: false, zIndex: maxZ + 1 }
    }));
  }, [maxZ]);

  const restoreWindow = useCallback((id) => {
    playSound(SOUNDS.click, volume / 100 * 0.15);
    setMaxZ(prev => prev + 1);
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], minimized: false, zIndex: maxZ + 1 }
    }));
  }, [maxZ, volume]);

  const updateWindowPosition = useCallback((id, position) => {
    setWindows(prev => ({
      ...prev,
      [id]: { ...prev[id], position }
    }));
  }, []);

  const handleShutdown = () => {
    playSound(SOUNDS.windowClose, volume / 100 * 0.15);
    setIsShutdown(true);
  };

  const handleBSOD = () => {
    playSound(SOUNDS.error, volume / 100 * 0.3);
    setShowBSOD(true);
    setCurrentEasterEgg('bsod');
  };

  const handleMatrixFromCmd = () => {
    setShowMatrix(true);
    setCurrentEasterEgg('matrix');
  };

  // Cursor style (only for desktop)
  useEffect(() => {
    if (!isMobile) {
      document.body.style.cursor = cursorStyle === 'wait' ? 'wait' : 'default';
      return () => { document.body.style.cursor = 'default'; };
    }
  }, [cursorStyle, isMobile]);

  // Render Mobile App for mobile devices
  if (isMobile) {
    return (
      <LazyMotion features={domAnimation}>
        <MobileApp />
        <Toaster position="top-center" richColors />
      </LazyMotion>
    );
  }

  if (isShutdown) {
    return <ShutdownScreen />;
  }

  // Desktop Experience
  return (
    <LazyMotion features={domAnimation}>
      <div className={`app ${crtEnabled ? 'crt-effect' : ''}`}>
        <AnimatePresence>
          {!introComplete && <Intro onComplete={handleIntroComplete} />}
        </AnimatePresence>
        
        <AnimatePresence>
          {introComplete && !showMatrix && !showBSOD && !showScreenSaver && (
            <Desktop 
              windows={windows}
              openWindow={openWindow}
              closeWindow={closeWindow}
              minimizeWindow={minimizeWindow}
              focusWindow={focusWindow}
              restoreWindow={restoreWindow}
              updateWindowPosition={updateWindowPosition}
              volume={volume}
              setVolume={setVolume}
              onShutdown={handleShutdown}
              onBSOD={handleBSOD}
              onMatrix={handleMatrixFromCmd}
              crtEnabled={crtEnabled}
              setCrtEnabled={setCrtEnabled}
            />
          )}
        </AnimatePresence>

        {/* Old Guided Tour removed - now using WelcomeGuide in Desktop.jsx */}

        <AnimatePresence>
          {showMatrix && (
            <MatrixRain onExit={() => setShowMatrix(false)} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showBSOD && (
            <BSOD onRecover={() => setShowBSOD(false)} />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showScreenSaver && (
            <ScreenSaver onExit={() => setShowScreenSaver(false)} />
          )}
        </AnimatePresence>

        {/* Keyboard Shortcuts Help Modal */}
        <KeyboardShortcuts 
          isOpen={showKeyboardHelp} 
          onClose={() => setShowKeyboardHelp(false)}
        />

        {/* Easter Egg Discovery Notification */}
        <EasterEggNotification 
          eggId={currentEasterEgg}
          onClose={() => setCurrentEasterEgg(null)}
        />

        <Toaster position="top-center" richColors />
      </div>
    </LazyMotion>
  );
}

// Main App with Router
function App() {
  return (
    <BrowserRouter>
      <Analytics />
      <CookieConsent />
      <ChatBubble />
      <Routes>
        <Route path="/" element={<MinimalistSite />} />
        <Route path="/win95" element={<MainApp />} />
        <Route path="/win98" element={<Win98Desktop />} />
        <Route path="/pedersen/tilbud" element={<ProposalPage />} />
        <Route path="/kampanje/:slug" element={<CampaignPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/leads" element={<AdminLeads />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/*" element={<MinimalistSite />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
